import { Box, Button, Flex, HStack, Text, useToast } from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaUsers } from 'react-icons/fa';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import SessionContext, { isInventoryOrgSession, isInvoicingOrgSession } from '../../../../contexts/SessionContext';
import { useGetLocations } from '../../../../hooks/useGetLocations';
import { useGetSettings } from '../../../../hooks/useGetSettings';
import { useInviteUser } from '../../../../hooks/useInviteUser';
import type { UserPermission } from '../../../../hooks/useEditDefaultPermissions';
import type { Role } from '../../../../hooks/useValidateSession';
import { getInvitePositionOptions } from '../../../../utils/invitePositionOptions';
import {
    SETTINGS_CONTENT_MAX_WIDTH,
    SettingsFlow,
    SettingsFlowForm,
    SettingsFlowOptions,
    useSettingsDetailCrumbs,
} from '../../../../components/settings/primitives';
import { copyUserPermission, sameUserPermission } from '../permissions/permissionCatalog';
import AddUserSingleStep from './addUsers/AddUserSingleStep';
import AddUsersTableStep from './addUsers/AddUsersTableStep';
import InvitePermissionsEditor from './addUsers/InvitePermissionsEditor';
import {
    buildInvitePayloads,
    effectiveInvitePermissions,
    newInviteDraft,
    type InviteDraft,
} from './addUsers/inviteDraft';

export interface AddUsersSectionProps {
    /** Role defaults from the users section, the base custom sets start from. */
    defaultPermissions: Partial<Record<Role, UserPermission>>;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<any, Error>>;
}

type AddMethod = 'single' | 'multi';
type StepId = 'method' | 'details';

/**
 * Add users, as a full-screen multi-step flow rather than the stack of modals
 * this used to be: pick whether you are adding one user or several, then fill in
 * the details with the whole window to work in — a form for one user, the
 * standard full-bleed table for many.
 *
 * Nothing is sent until Save, which posts one invite per completed row.
 * Permissions are part of the invite here, so a user can arrive with a custom
 * set instead of being invited and then edited.
 */
const AddUsersSection = ({ defaultPermissions, refetch }: AddUsersSectionProps) => {
    const navigate = useNavigate();
    const toast = useToast();
    const session = useContext(SessionContext);
    const isInvoicingOrg = isInvoicingOrgSession(session);
    const isInventoryOrg = isInventoryOrgSession(session);
    const { data: settingsData } = useGetSettings();
    const { data: locationsData } = useGetLocations();
    const inviteUser = useInviteUser();

    const positionOptions = useMemo(() => {
        if (isInvoicingOrg) return [{ value: 'invoicing' as Role, label: 'Invoicing' }];
        return getInvitePositionOptions({
            includeTechnician: settingsData?.inventoryEnabled === true,
            includeInvoicing: settingsData?.invoicingEnabled === true,
            inventoryOrg: isInventoryOrg,
        });
    }, [isInvoicingOrg, isInventoryOrg, settingsData?.inventoryEnabled, settingsData?.invoicingEnabled]);

    // Invoicing-only orgs have one position, so it is filled in from the start
    // and the field is locked rather than asked for.
    const initialPosition: Role | '' = isInvoicingOrg ? 'invoicing' : '';

    const [method, setMethod] = useState<AddMethod | null>(null);
    const [stepId, setStepId] = useState<StepId>('method');
    const [single, setSingle] = useState<InviteDraft>(() =>
        newInviteDraft({ position: initialPosition }),
    );
    const [rows, setRows] = useState<InviteDraft[]>(() => [
        newInviteDraft({ position: initialPosition }),
    ]);
    /**
     * Rows whose custom permissions are being edited on their own screen. The
     * multi step has no room for a matrix in a cell, so it opens this instead.
     */
    const [permissionsTargetIds, setPermissionsTargetIds] = useState<string[] | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const payloads = useMemo(
        () => buildInvitePayloads(method === 'single' ? [single] : rows),
        [method, single, rows],
    );

    const closeFlow = () => navigate('/settings/users');

    useSettingsDetailCrumbs(
        permissionsTargetIds
            ? [
                  { label: 'Add users', onClick: () => setPermissionsTargetIds(null) },
                  { label: 'Custom permissions' },
              ]
            : [{ label: 'Add users' }],
    );

    const steps = [
        { id: 'method', label: 'Method' },
        {
            id: 'details',
            label: method === 'multi' ? 'Users' : 'User details',
            // Until a method is picked there is nothing to show on step 2.
            isDisabled: method === null,
        },
    ];

    const handleSelectMethod = (id: string) => {
        setMethod(id as AddMethod);
        setStepId('details');
    };

    const handleStepSelect = (id: string) => {
        if (id === 'details' && method === null) return;
        setPermissionsTargetIds(null);
        setStepId(id as StepId);
    };

    const handleSave = async () => {
        if (payloads.length === 0) return;

        setIsSaving(true);
        const results = await Promise.allSettled(
            payloads.map((payload) => inviteUser.mutateAsync(payload)),
        );
        setIsSaving(false);

        const failed = results.filter((result) => result.status === 'rejected').length;
        const sent = payloads.length - failed;

        if (failed === 0) {
            toast({
                title: `${sent} invitation${sent === 1 ? '' : 's'} sent`,
                description: 'They will appear as pending users until they sign up.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            refetch();
            closeFlow();
            return;
        }

        toast({
            title: `${sent} sent, ${failed} failed`,
            description:
                'Some invitations could not be sent. Check for addresses that are already invited and try again.',
            status: 'error',
            duration: 6000,
            isClosable: true,
        });
        refetch();
    };

    // Targets keep their own positions, so the matrix opens on the first one's
    // effective set; editing then writes that same set to all of them.
    const permissionsTargets = permissionsTargetIds
        ? rows.filter((row) => permissionsTargetIds.includes(row.id))
        : [];
    const permissionsValue =
        permissionsTargets.length > 0
            ? effectiveInvitePermissions(permissionsTargets[0], defaultPermissions)
            : copyUserPermission();

    const applyPermissionsToTargets = (next: UserPermission) => {
        setRows((prev) =>
            prev.map((row) => {
                if (!permissionsTargetIds?.includes(row.id)) return row;
                // A set identical to the row's own role defaults is just
                // "Default" again, so the cell never reads Custom for nothing.
                const roleDefault = copyUserPermission(
                    row.position ? defaultPermissions[row.position] : undefined,
                );
                return {
                    ...row,
                    customPermissions: sameUserPermission(next, roleDefault)
                        ? null
                        : copyUserPermission(next),
                };
            }),
        );
    };

    const saveLabel =
        payloads.length === 0
            ? 'Save'
            : `Save ${payloads.length} user${payloads.length === 1 ? '' : 's'}`;

    return (
        <SettingsFlow
            steps={steps}
            activeStepId={stepId}
            onStepSelect={handleStepSelect}
            onCancel={closeFlow}
            onBack={
                permissionsTargetIds
                    ? () => setPermissionsTargetIds(null)
                    : stepId === 'details'
                        ? () => setStepId('method')
                        : undefined
            }
            onNext={() => setStepId('details')}
            isNextDisabled={method === null}
            onSave={() => void handleSave()}
            saveLabel={saveLabel}
            isSaveDisabled={payloads.length === 0}
            isSaving={isSaving}
            primaryAction={
                permissionsTargetIds
                    ? { label: 'Done', onClick: () => setPermissionsTargetIds(null) }
                    : undefined
            }
        >
            {stepId === 'method' && (
                <SettingsFlowOptions
                    title="How do you want to add users?"
                    selectedId={method ?? undefined}
                    onSelect={handleSelectMethod}
                    options={[
                        {
                            id: 'single',
                            title: 'Single user',
                            description:
                                'Invite one user, with their position, locations, and permissions.',
                            icon: FaUser as React.ElementType,
                        },
                        {
                            id: 'multi',
                            title: 'Multiple users',
                            description:
                                'Build a table of users, or import the invite spreadsheet.',
                            icon: FaUsers as React.ElementType,
                        },
                    ]}
                />
            )}

            {stepId === 'details' && method === 'single' && (
                <AddUserSingleStep
                    draft={single}
                    onChange={setSingle}
                    positionOptions={positionOptions}
                    isPositionLocked={isInvoicingOrg}
                    defaultPermissions={defaultPermissions}
                />
            )}

            {stepId === 'details' && method === 'multi' && !permissionsTargetIds && (
                <AddUsersTableStep
                    rows={rows}
                    onChange={setRows}
                    positionOptions={positionOptions}
                    isPositionLocked={isInvoicingOrg}
                    locations={locationsData?.locations ?? []}
                    onEditPermissions={setPermissionsTargetIds}
                />
            )}

            {stepId === 'details' && method === 'multi' && permissionsTargetIds && (
                <SettingsFlowForm>
                    <Flex justify="space-between" align="flex-start" gap={4} pb={4}>
                        <Box>
                            <Text fontSize="md" fontWeight="semibold" color="gray.800">
                                Custom permissions
                            </Text>
                            <Text fontSize="sm" color="gray.500" mt={1}>
                                {permissionsTargets.length === 1
                                    ? `Applies to ${permissionsTargets[0].email || 'this user'}. Checked values come from their position's defaults.`
                                    : `Applies to ${permissionsTargets.length} selected users. Checked values come from the first user's defaults.`}
                            </Text>
                        </Box>
                        <HStack spacing={2} flexShrink={0}>
                            <Button
                                size="sm"
                                variant="outlineGray"
                                onClick={() =>
                                    setRows((prev) =>
                                        prev.map((row) =>
                                            permissionsTargetIds.includes(row.id)
                                                ? { ...row, customPermissions: null }
                                                : row,
                                        ),
                                    )
                                }
                            >
                                Reset to defaults
                            </Button>
                        </HStack>
                    </Flex>
                    <Box maxW={SETTINGS_CONTENT_MAX_WIDTH}>
                        <InvitePermissionsEditor
                            value={permissionsValue}
                            onChange={applyPermissionsToTargets}
                        />
                    </Box>
                </SettingsFlowForm>
            )}

            {stepId === 'details' && method === null && (
                // Reachable only by deep-linking into the flow with no choice
                // made; the step tab is otherwise disabled.
                <SettingsFlowForm>
                    <Text fontSize="sm" color="gray.500">
                        Pick how you want to add users first.
                    </Text>
                </SettingsFlowForm>
            )}
        </SettingsFlow>
    );
};

export default AddUsersSection;
